import React from 'react';

interface PhysicalExamProps {
  data: {
    motor_function: string;
    sensory_function: string;
    distal_pulse: string;
    capillary_fill: string;
    compartment_syndrome_suspected: boolean;
    edema: string;
    ecchymosis: string;
    deformity: string;
    range_of_motion_active: string;
    range_of_motion_passive: string;
    stability: string;
    weight_bearing: string;
  };
  onChange: (field: string, value: any) => void;
}

const NEURO_STATUS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'conservada', label: 'Conservada' },
  { value: 'disminuida', label: 'Disminuida' },
  { value: 'ausente', label: 'Ausente' },
];

const PULSE_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'presente', label: 'Presente' },
  { value: 'disminuido', label: 'Disminuido' },
  { value: 'ausente', label: 'Ausente' },
];

const CAPILLARY_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: '<2 seg', label: '< 2 seg' },
  { value: '2-3 seg', label: '2-3 seg' },
  { value: '>3 seg', label: '> 3 seg' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'leve', label: 'Leve' },
  { value: 'moderado', label: 'Moderado' },
  { value: 'severo', label: 'Severo' },
];

const SEVERITY_F_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'leve', label: 'Leve' },
  { value: 'moderada', label: 'Moderada' },
  { value: 'severa', label: 'Severa' },
];

const DEFORMITY_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'ninguna', label: 'Ninguna' },
  { value: 'leve', label: 'Leve' },
  { value: 'moderada', label: 'Moderada' },
  { value: 'severa', label: 'Severa' },
];

const ROM_ACTIVE_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'completo', label: 'Completo' },
  { value: 'limitado', label: 'Limitado' },
  { value: 'imposible', label: 'Imposible' },
];

const ROM_PASSIVE_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'completo', label: 'Completo' },
  { value: 'limitado', label: 'Limitado' },
  { value: 'doloroso', label: 'Doloroso' },
  { value: 'imposible', label: 'Imposible' },
];

const STABILITY_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'estable', label: 'Estable' },
  { value: 'inestable', label: 'Inestable' },
];

const WEIGHT_BEARING_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'completo', label: 'Completo' },
  { value: 'parcial con ayuda', label: 'Parcial con Ayuda' },
  { value: 'imposible', label: 'Imposible' },
];

const PhysicalExam: React.FC<PhysicalExamProps> = ({ data, onChange }) => {
  return (
    <div className="card mb-3">
      <div className="card-body">
        <h5 className="card-title">
          <i className="fa fa-heartbeat me-1"></i>
          Examen Físico
        </h5>

        <h6 className="text-muted fw-semibold mb-3 mt-3">Examen Neurovascular</h6>
        <div className="row">
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Función Motora</label>
              <select
                className="form-select"
                value={data.motor_function}
                onChange={e => onChange('motor_function', e.target.value)}
              >
                {NEURO_STATUS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Función Sensitiva</label>
              <select
                className="form-select"
                value={data.sensory_function}
                onChange={e => onChange('sensory_function', e.target.value)}
              >
                {NEURO_STATUS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Pulso Distal</label>
              <select
                className="form-select"
                value={data.distal_pulse}
                onChange={e => onChange('distal_pulse', e.target.value)}
              >
                {PULSE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Llenado Capilar</label>
              <select
                className="form-select"
                value={data.capillary_fill}
                onChange={e => onChange('capillary_fill', e.target.value)}
              >
                {CAPILLARY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-3">
            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="compartment_syndrome_suspected"
                  checked={data.compartment_syndrome_suspected}
                  onChange={e => onChange('compartment_syndrome_suspected', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="compartment_syndrome_suspected">
                  Sospecha de Síndrome Compartimental
                </label>
              </div>
            </div>
          </div>
        </div>

        <h6 className="text-muted fw-semibold mb-3 mt-4">Examen Físico</h6>
        <div className="row">
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Edema</label>
              <select
                className="form-select"
                value={data.edema}
                onChange={e => onChange('edema', e.target.value)}
              >
                {SEVERITY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Equimosis</label>
              <select
                className="form-select"
                value={data.ecchymosis}
                onChange={e => onChange('ecchymosis', e.target.value)}
              >
                {SEVERITY_F_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Deformidad</label>
              <select
                className="form-select"
                value={data.deformity}
                onChange={e => onChange('deformity', e.target.value)}
              >
                {DEFORMITY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="mb-3">
              <label className="form-label">Estabilidad</label>
              <select
                className="form-select"
                value={data.stability}
                onChange={e => onChange('stability', e.target.value)}
              >
                {STABILITY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Arco de Movimiento Activo</label>
              <select
                className="form-select"
                value={data.range_of_motion_active}
                onChange={e => onChange('range_of_motion_active', e.target.value)}
              >
                {ROM_ACTIVE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Arco de Movimiento Pasivo</label>
              <select
                className="form-select"
                value={data.range_of_motion_passive}
                onChange={e => onChange('range_of_motion_passive', e.target.value)}
              >
                {ROM_PASSIVE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Apoyo</label>
              <select
                className="form-select"
                value={data.weight_bearing}
                onChange={e => onChange('weight_bearing', e.target.value)}
              >
                {WEIGHT_BEARING_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhysicalExam;
